import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  FiSettings, FiDollarSign, FiChevronDown, FiChevronRight, FiHome, 
  FiUser, FiBriefcase, FiUsers, FiUserCheck, FiMail, FiBookOpen, 
  FiLayers, FiClipboard, FiLogOut, FiTrendingUp, FiBookmark, 
  FiAward, FiTarget, FiDatabase, FiX, FiActivity, FiMapPin, FiCreditCard
} from 'react-icons/fi';

const sidebarConfig = [
  {
    label: 'Genel',
    icon: FiHome,
    color: 'bg-blue-500',
    items: [
      { label: 'Anasayfa', path: '/admin/', icon: FiHome },
      { label: 'Danışman Listesi', path: '/admin/advisors', icon: FiUser },
      { label: 'Şirket Listesi', path: '/admin/companies', icon: FiBriefcase },
      { label: 'İl / İlçe Yönetimi', path: '/admin/location-management', icon: FiMapPin },
      { label: 'Banka Yönetimi', path: '/admin/bank-management', icon: FiCreditCard },
    ]
  },
  {
    label: 'Öğrenci Yönetimi',
    icon: FiUsers,
    color: 'bg-purple-500',
    items: [
      { label: 'Tüm Öğrenciler', path: '/admin/student-management/all', icon: FiUsers },
    ]
  },
  {
    label: 'Öğretmen Yönetimi',
    icon: FiUserCheck,
    color: 'bg-green-500',
    items: [
      { label: 'Tüm Öğretmenler', path: '/admin/teacher-management/all', icon: FiUsers },
      { label: 'Geçmiş Dersler', path: '/admin/teacher-management/past-lessons', icon: FiLayers },
      { label: 'Yeni Ders Ücretleri', path: '/admin/teacher-management/kurs-satis', icon: FiLayers },
    ]
  },
  {
    label: 'Mail & SMS',
    icon: FiMail,
    color: 'bg-orange-500',
    items: [
      { label: 'Mail & SMS Gönder', path: '/admin/mail-sms-management', icon: FiMail },
    ]
  },
  {
    label: 'Ders Yönetimi',
    icon: FiBookOpen,
    color: 'bg-indigo-500',
    items: [
      { label: 'Ders Grupları', path: '/admin/lesson-groups', icon: FiLayers }
    ]
  },
  {
    label: 'Kullanıcı Yönetimi',
    icon: FiUsers,
    color: 'bg-pink-500',
    items: [
      { label: 'Admin Listesi', path: '/admin/management', icon: FiUserCheck } 
    ]
  },
  {
    label: 'Muhasebe',
    icon: FiTrendingUp,
    color: 'bg-emerald-500',
    items: [
      { label: 'Avans Yönetimi', path: '/admin/accounting/avans', icon: FiDollarSign },
      { label: 'Gelen Ödemeler', path: '/admin/accounting/gelen-odemeler', icon: FiDollarSign },
      { 
        label: 'Raporlar', 
        icon: FiClipboard,
        hasSubmenu: true,
        submenu: [
          { label: 'Öğretmen Raporu', path: '/admin/accounting/reports/teacher', icon: FiUsers },
          { label: 'Danışmanlar Raporu', path: '/admin/accounting/reports/danismanlar', icon: FiTrendingUp },
          { label: 'Şirketler Raporu', path: '/admin/accounting/reports/companies', icon: FiBriefcase }
        ]
      }
    ]
  },
  {
    label: 'Yapılacaklar',
    icon: FiTarget,
    color: 'bg-violet-500',
    items: [
      { label: 'Görev Listesi', path: '/admin/todo', icon: FiClipboard }
    ]
  },
  {
    label: 'Site Yönetimi',
    icon: FiSettings,
    color: 'bg-gray-600',
    items: [
      { label: 'Site Ayarları', path: '/admin/site-settings', icon: FiSettings }
    ]
  }
];

const Sidebar = ({ stats = { studentCount: 0, teacherCount: 0, lessonCount: 0 }, isOpen, onClose }) => {
  const [openSections, setOpenSections] = useState({});
  const location = useLocation();
  const navigate = useNavigate();
  
  // Süper admin kontrolü
  const userRole = localStorage.getItem('adminRole');
  const menuPermissions = JSON.parse(localStorage.getItem('menuPermissions') || '[]');
  
  // Veritabanı menüsünü sadece süper adminlere ekle
  const allSidebarConfig = [...sidebarConfig];
  if (userRole === 'superadmin') {
    allSidebarConfig.push({
      label: 'Veritabanı',
      icon: FiDatabase,
      color: 'bg-red-500',
      items: [
        { label: 'Veritabanı Yönetimi', path: '/admin/database', icon: FiDatabase }
      ]
    });
  }

  // Süper admin ise tüm menüleri göster, değilse yetkilere göre filtrele
  let displaySidebarConfig = allSidebarConfig;
  
  if (userRole !== 'superadmin') {
    const menuKeys = {
      '/admin/': 'dashboard',
      '/admin/advisors': 'advisors',
      '/admin/companies': 'companies',
      '/admin/location-management': 'location-management',
      '/admin/bank-management': 'bank-management', // BANKA YÖNETİMİ HARİÇ TUTULMAYACAK
      '/admin/student-management/all': 'students',
      '/admin/teacher-management/all': 'teachers',
      '/admin/teacher-management/past-lessons': 'past-lessons',
      '/admin/teacher-management/kurs-satis': 'kurs-satis',
      '/admin/mail-sms-management': 'mail-sms',
      '/admin/lesson-groups': 'lesson-groups',
      '/admin/management': 'admin-list',
      '/admin/accounting/avans': 'avans',
      '/admin/accounting/gelen-odemeler': 'gelen-odemeler',
      '/admin/accounting/reports/teacher': 'teacher-report',
      '/admin/accounting/reports/danismanlar': 'danismanlar-report',
      '/admin/accounting/reports/companies': 'companies-report',
      '/admin/todo': 'todo',
      '/admin/site-settings': 'site-settings',
      '/admin/database': 'database'
    };
    
    const hasMenuPermission = (menuKey) => {
      // BANKA YÖNETİMİ VE SİTE AYARLARI İÇİN HER ZAMAN TRUE DÖN
      if (menuKey === 'bank-management') return true;
      if (menuKey === 'site-settings') return true;
      return menuPermissions.includes(menuKey);
    };
    
    displaySidebarConfig = allSidebarConfig.map(section => {
      const filteredItems = section.items.filter(item => {
        const menuKey = menuKeys[item.path];
        if (!menuKey) return true;
        
        if (item.hasSubmenu && item.submenu) {
          const hasSubmenuPermission = item.submenu.some(subItem => {
            const subKey = menuKeys[subItem.path];
            return !subKey || hasMenuPermission(subKey);
          });
          return hasSubmenuPermission;
        }
        
        return hasMenuPermission(menuKey);
      });
      
      const itemsWithFilteredSubmenu = filteredItems.map(item => {
        if (item.hasSubmenu && item.submenu) {
          return {
            ...item,
            submenu: item.submenu.filter(subItem => {
              const subKey = menuKeys[subItem.path];
              return !subKey || hasMenuPermission(subKey);
            })
          };
        }
        return item;
      });
      
      return {
        ...section,
        items: itemsWithFilteredSubmenu
      };
    }).filter(section => section.items.length > 0);
  }

  useEffect(() => {
    // Aktif menü öğesini otomatik aç
    displaySidebarConfig.forEach(section => {
      const hasActive = section.items.some(item => {
        if (item.hasSubmenu && item.submenu) {
          return item.submenu.some(subItem => location.pathname === subItem.path);
        }
        return location.pathname === item.path;
      });
      
      if (hasActive) {
        setOpenSections(prev => ({ ...prev, [section.label]: true }));
        
        // Alt menüyü de aç
        section.items.forEach(item => {
          if (item.hasSubmenu && item.submenu) {
            const hasActiveSubmenu = item.submenu.some(subItem => location.pathname === subItem.path);
            if (hasActiveSubmenu) {
              setOpenSections(prev => ({ ...prev, [item.label]: true }));
            }
          }
        });
      }
    });
  }, [location]);

  const toggleSection = (key) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleNavClick = (path) => {
    navigate(path);
    // Mobilde navigasyon sonrası sidebar'ı kapat
    if (window.innerWidth < 1024 && onClose) {
      onClose();
    }
  };

  return (
    <aside className={`
      fixed inset-y-0 left-0 z-40 w-72 bg-gray-900 
      transform transition-transform duration-300 ease-in-out
      lg:translate-x-0 lg:static lg:inset-0
      ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      h-screen overflow-hidden
    `}>
      <div className="flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <FiBookOpen className="text-white text-xl" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Özel Ders</h2>
              <p className="text-xs text-gray-400">
                {userRole === 'superadmin' ? 'Süper Admin' : 'Yönetim Paneli'}
              </p>
            </div>
          </div>
          {/* Mobile close button */}
          <button
            onClick={onClose}
            className="lg:hidden p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Stats */}
        <div className="px-4 py-3 border-b border-gray-800">
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => handleNavClick('/admin/student-management/all')}
              className="bg-gray-800 hover:bg-gray-700 rounded-lg p-2 text-center transition-colors"
            >
              <div className="text-xl font-bold text-blue-400">{stats.studentCount}</div>
              <div className="text-xs text-gray-400">Öğrenci</div>
            </button>
            <button
              onClick={() => handleNavClick('/admin/teacher-management/all')}
              className="bg-gray-800 hover:bg-gray-700 rounded-lg p-2 text-center transition-colors"
            >
              <div className="text-xl font-bold text-green-400">{stats.teacherCount}</div>
              <div className="text-xs text-gray-400">Öğretmen</div>
            </button>
            <button
              onClick={() => handleNavClick('/admin/lesson-groups')}
              className="bg-gray-800 hover:bg-gray-700 rounded-lg p-2 text-center transition-colors"
            >
              <div className="text-xl font-bold text-purple-400">{stats.lessonCount}</div>
              <div className="text-xs text-gray-400">Ders</div>
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav-scroll space-y-2">
          {displaySidebarConfig.map((section, idx) => {
            const isOpen = openSections[section.label];
            
            return (
              <div key={idx} className="space-y-1">
                <button
                  onClick={() => toggleSection(section.label)}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${section.color}`}>
                      {React.createElement(section.icon, { className: 'text-white text-sm' })}
                    </div>
                    <span className="text-sm font-medium">{section.label}</span>
                  </div>
                  {section.items.length > 0 && (
                    <FiChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  )}
                </button>
                
                {/* Items */}
                {isOpen && (
                  <div className="ml-4 space-y-1">
                    {section.items.map((item, itemIdx) => (
                      <div key={itemIdx}>
                        {item.hasSubmenu ? (
                          <>
                            <button
                              onClick={() => toggleSection(item.label)}
                              className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
                            >
                              <div className="flex items-center space-x-2">
                                {React.createElement(item.icon, { className: 'w-4 h-4' })}
                                <span className="text-sm">{item.label}</span>
                              </div>
                              <FiChevronDown className={`w-3 h-3 transition-transform ${openSections[item.label] ? 'rotate-180' : ''}`} />
                            </button>
                            
                            {openSections[item.label] && (
                              <div className="ml-6 space-y-1 mt-1">
                                {item.submenu.map((subItem, subIdx) => (
                                  <NavLink
                                    key={subIdx}
                                    to={subItem.path}
                                    onClick={() => handleNavClick(subItem.path)}
                                    className={({ isActive }) =>
                                      `flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                        isActive 
                                          ? 'bg-gray-800 text-white' 
                                          : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                                      }`
                                    }
                                  >
                                    {React.createElement(subItem.icon, { className: 'w-3 h-3' })}
                                    <span>{subItem.label}</span>
                                  </NavLink>
                                ))}
                              </div>
                            )}
                          </>
                        ) : (
                          <NavLink
                            to={item.path}
                            onClick={() => handleNavClick(item.path)}
                            className={({ isActive }) =>
                              `flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${
                                isActive 
                                  ? 'bg-gray-800 text-white' 
                                  : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                              }`
                            }
                          >
                            {React.createElement(item.icon, { className: 'w-4 h-4' })}
                            <span className="text-sm">{item.label}</span>
                          </NavLink>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-800">
          <button
            onClick={() => {
              localStorage.clear();
              navigate('/admin/login');
            }}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            <FiLogOut className="w-4 h-4" />
            <span className="text-sm">Çıkış Yap</span>
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;